/**
 * L'écran d'un parc — véhicules, remorques, chauffeurs.
 *
 * **Un seul écran pour les trois**, paramétré par un descripteur, comme
 * l'annuaire l'est pour les siens et pour la même raison : trois fichiers
 * jumeaux de six cents lignes finiraient par diverger sans que personne ne le
 * voie. `parc.ts` dit pourquoi ce n'est pas `EcranAnnuaire`.
 *
 * Même découpe que l'annuaire, et c'est délibéré :
 *
 *   - `VueParc` ne fait que DESSINER. On lui donne un état, elle rend du HTML.
 *     C'est ce qui permet de la rendre et de la regarder hors d'un navigateur,
 *     avec n'importe quel état — liste vide, fiche en panne, ligne désactivée ;
 *   - `EcranParc` tient l'état, appelle l'API, et lui passe le résultat.
 *
 * Ce que ce fichier ne fait pas : calculer. Le filtrage, le décompte, la
 * validation et la différence sont dans `parc.ts`, en fonctions pures.
 *
 * PAS D'ONGLETS SUR LA FICHE. Une fiche de client en a jusqu'à cinq parce qu'un
 * client porte des contacts, une couverture, des conditions. Un véhicule porte
 * ses trois champs. Ses attelages et ses arrêts existent, mais ils vivent dans
 * `affectations` et `indisponibilites` et se règlent depuis leurs écrans — les
 * afficher ici demanderait de décider qui, de cet écran ou de l'autre, en est
 * responsable, et la réponse est l'autre.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  changerActiviteParc,
  creerLigneParc,
  lireLigneParc,
  lireParc,
  lireListeReglee,
  lireVehicules,
  modifierLigneParc,
  supprimerLigneParc,
  type EchecApi,
  type Vehicule,
} from './api.js';
import { capitale, type Choix } from './annuaire.js';
import { FormulaireFiche } from './FormulaireFiche.js';
import {
  aucun,
  decompte,
  difference,
  e,
  filtrer,
  inactif,
  lisible,
  nommer,
  nouveau,
  pourCreation,
  ranger,
  saisieDepuis,
  un,
  saisieVide,
  validerSaisie,
  type LigneParc,
  type Parc,
  type Saisie,
} from './parc.js';

// ---------------------------------------------------------------------------
// Les états
// ---------------------------------------------------------------------------

type EtatListe =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'prete'; readonly lignes: readonly LigneParc[] }
  | { readonly nom: 'panne' };

type EtatFiche =
  | { readonly nom: 'aucune' }
  | { readonly nom: 'prete'; readonly id: string; readonly ligne: LigneParc }
  | { readonly nom: 'panne'; readonly id: string; readonly cause: EchecApi };

type Edition =
  | { readonly nom: 'aucune' }
  | {
      readonly nom: 'creation' | 'modification';
      readonly id: string | null;
      readonly initiale: Saisie;
      readonly saisie: Saisie;
      readonly fautes: Readonly<Record<string, string>>;
      readonly refus: string | null;
      readonly envoi: boolean;
    };

export interface ActionsParc {
  readonly confirme: boolean;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandBasculerActivite: () => void;
  readonly quandDemanderSuppression: () => void;
  readonly quandSupprimer: () => void;
  readonly quandAnnuler: () => void;
}

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette ligne n’existe plus. Elle a pu être supprimée entre-temps.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Cette immatriculation est déjà enregistrée dans votre société.',
  invalide: 'Une valeur saisie n’est pas acceptée. Vérifiez les champs.',
  // Un parc ne pose aucune période : ce refus ne vient jamais d'ici. La clé
  // existe parce que `EchecApi` la porte, et le message dit quelque chose de
  // vrai plutôt que de rester vide.
  conflit: 'Cette période en recouvre une autre.',
  encore_actif: 'Désactivez cette ligne avant de la supprimer.',
  encore_rattache:
    'Impossible de supprimer : cette ligne est encore attelée. Détachez-la d’abord, depuis l’écran Affectation.',
  // Les quatre refus du planning. Ils n'arrivent pas sur cet écran — la carte
  // doit rester exhaustive, c'est ce qui garantit qu'aucun refus de l'API ne
  // s'affichera un jour sous forme de code brut.
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape : rien à poser sur un camion.',
  etape_validee:
    'Une étape de cet ordre est déjà validée : ses dates ne se déplacent plus, et son camion ne se retire plus.',
  jour_sans_etape: 'Cet ordre n’a aucune étape ce jour-là. Rechargez l’écran.',
  // Ne vient jamais d'ici. La carte reste exhaustive, comme les autres.
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
  // LES SIX REFUS DE LA FACTURATION, ajoutés le 09/09/2026. Ils n'arrivent pas
  // sur cet écran — la carte reste exhaustive, c'est ce qui garantit qu'aucun
  // refus de l'API ne s'affichera un jour sous forme de code brut.
  litige_ouvert: 'Cet ordre porte un litige ouvert : il ne se facture pas.',
  deja_facture: 'Cet ordre est déjà porté par une facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise: 'Cette pièce est émise : elle se corrige par un avoir.',
  facture_brouillon: 'Un brouillon ne s’envoie pas : validez-le d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas.',
  // AJOUTÉ LE 09/09/2026. Un ordre parti en facturation ne se modifie plus :
  // il faut le retirer de la facturation d'abord.
  ordre_en_facturation:
    'Cet ordre est parti en facturation : retirez-le de la facturation avant de le modifier.',
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Chacun dit la SUITE,
  // pas seulement le refus : c'est ce qui distingue un état d'une saisie fautive.
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};

// ---------------------------------------------------------------------------
// Le panneau d'une ligne
// ---------------------------------------------------------------------------

function Panneau({
  parc,
  etat,
  gabarits,
  types,
  quandFermer,
  quandModifier,
  actions,
}: {
  readonly parc: Parc;
  readonly etat: EtatFiche;
  readonly gabarits: readonly Vehicule[];
  /** Les types de véhicule de la société, pour lire une clé — 04/09/2026. */
  readonly types: readonly Choix[];
  readonly quandFermer: () => void;
  readonly quandModifier: () => void;
  readonly actions: ActionsParc;
}) {
  if (etat.nom === 'aucune') return null;

  if (etat.nom === 'panne') {
    return (
      <aside className="panneau">
        <div className="panneau-haut">
          <div className="panneau-titre">
            <div>
              <div className="panneau-nom">Fiche indisponible</div>
            </div>
            <button className="fermer" type="button" onClick={quandFermer}>
              Fermer ✕
            </button>
          </div>
          <p className="bandeau">{REFUS[etat.cause]}</p>
        </div>
      </aside>
    );
  }

  const { ligne } = etat;

  return (
    <aside className="panneau">
      <div className="panneau-haut">
        {!ligne.actif && (
          <p className="bandeau">
            {capitale(parc.singulier)} désactivé{e(parc)}. {parc.noteDesactive}
          </p>
        )}
        <div className="panneau-titre">
          <div>
            <div className="panneau-nom">{nommer(ligne, parc)}</div>
          </div>
          <button className="fermer" type="button" onClick={quandFermer}>
            Fermer ✕
          </button>
        </div>

        {actions.refus !== null && <p className="bandeau">{actions.refus}</p>}

        {/* Une ligne ACTIVE ne se supprime pas : on désactive d'abord. Même
            règle que l'annuaire, et l'API répond `encore_actif` si on passe
            outre. */}
        <div className="actions">
          <button
            className={ligne.actif ? 'bouton' : 'bouton bouton-discret'}
            type="button"
            disabled={actions.envoi}
            onClick={quandModifier}
          >
            Modifier
          </button>
          <button
            className={ligne.actif ? 'bouton bouton-risque' : 'bouton bouton-discret'}
            type="button"
            disabled={actions.envoi}
            onClick={actions.quandBasculerActivite}
          >
            {ligne.actif ? 'Désactiver' : 'Réactiver'}
          </button>
          {!ligne.actif && (
            <button
              className="bouton bouton-risque"
              type="button"
              disabled={actions.envoi}
              onClick={actions.quandDemanderSuppression}
            >
              Supprimer
            </button>
          )}
        </div>

        {actions.confirme && (
          <div className="confirmation">
            <p className="empechement">
              Supprimer définitivement <strong>{nommer(ligne, parc)}</strong> ? La
              suppression sera refusée si des attelages s’y rattachent encore. Cette
              action est sans retour.
            </p>
            <div className="actions">
              <button
                className="bouton bouton-risque"
                type="button"
                disabled={actions.envoi}
                onClick={actions.quandSupprimer}
              >
                {actions.envoi ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
              <button
                className="bouton bouton-discret"
                type="button"
                disabled={actions.envoi}
                onClick={actions.quandAnnuler}
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="panneau-corps">
        <dl className="paires">
          {parc.champs.map((champ) => {
            const brut = lisible(ligne, champ.cle);
            const lu =
              brut === null
                ? brut
                : champ.liste === 'gabarits'
                  ? (gabarits.find((g) => g.code === brut)?.libelle ?? brut)
                  : champ.liste === 'types_vehicule'
                    ? (types.find((t) => t.valeur === brut)?.texte ?? brut)
                    : brut;
            return (
              <div className="paire" key={champ.cle}>
                <dt>{champ.etiquette}</dt>
                {lu === null ? (
                  <dd className="absent">non renseigné</dd>
                ) : (
                  <dd className={champ.chiffre === true ? 'chiffre' : undefined}>
                    {lu}
                    {champ.unite !== undefined && (
                      <span className="sourdine"> {champ.unite}</span>
                    )}
                  </dd>
                )}
              </div>
            );
          })}
        </dl>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

export interface ProprietesVueParc {
  readonly parc: Parc;
  readonly liste: EtatListe;
  readonly fiche: EtatFiche;
  readonly edition: Edition;
  readonly gabarits: readonly Vehicule[];
  /** Les types de véhicule de la société, pour lire une clé — 04/09/2026. */
  readonly types: readonly Choix[];
  readonly recherche: string;
  readonly quandRecherche: (texte: string) => void;
  readonly quandOuvrir: (id: string) => void;
  readonly quandFermer: () => void;
  readonly quandCreer: () => void;
  readonly quandModifier: () => void;
  readonly quandChamp: (cle: string, valeur: string) => void;
  readonly quandEnregistrer: () => void;
  readonly quandAnnuler: () => void;
  readonly actions: ActionsParc;
}

/** Le dessin, et rien d'autre : aucun appel réseau, aucun état gardé. */
export function VueParc({
  parc,
  liste,
  fiche,
  edition,
  gabarits,
  types,
  recherche,
  quandRecherche,
  quandOuvrir,
  quandFermer,
  quandCreer,
  quandModifier,
  quandChamp,
  quandEnregistrer,
  quandAnnuler,
  actions,
}: ProprietesVueParc) {
  const lignes = liste.nom === 'prete' ? ranger(liste.lignes, parc) : [];
  const visibles = filtrer(lignes, recherche, parc);
  const ouverte = fiche.nom !== 'aucune' || edition.nom !== 'aucune';

  const choixGabarits: readonly Choix[] = gabarits.map((g) => ({
    valeur: g.code,
    texte: g.libelle,
  }));

  return (
    <div className={ouverte ? 'corps ouvert' : 'corps'}>
      <section className="liste">
        <div className="barre-outils">
          <input
            className="recherche"
            type="search"
            placeholder={`Rechercher — ${parc.rechercheDite}`}
            aria-label={`Rechercher ${un(parc)} ${parc.singulier}`}
            value={recherche}
            onChange={(e) => quandRecherche(e.target.value)}
          />
          <button className="bouton" type="button" onClick={quandCreer}>
            Créer {un(parc)} {parc.singulier}
          </button>
        </div>

        {/* Le décompte ne s'affiche que s'il y a des lignes à compter : « 0
            véhicule » posé au-dessus de « Aucun véhicule n'est enregistré »
            fait doublon et sonne creux. */}
        {visibles.length > 0 && <p className="decompte">{decompte(visibles, parc)}</p>}

        {liste.nom === 'chargement' && <p className="attente">Chargement…</p>}

        {liste.nom === 'panne' && (
          <div className="vide">
            <p>Stafa ne répond pas. Vérifiez votre connexion et réessayez.</p>
          </div>
        )}

        {liste.nom === 'prete' && lignes.length === 0 && (
          <div className="vide">
            <p>
              {aucun(parc)} {parc.singulier} n’est enregistré{e(parc)}.
            </p>
            <button className="bouton" type="button" onClick={quandCreer}>
              Créer {un(parc)} {parc.singulier}
            </button>
          </div>
        )}

        {liste.nom === 'prete' && lignes.length > 0 && visibles.length === 0 && (
          <div className="vide">
            <p>
              {aucun(parc)} {parc.singulier} ne correspond à « {recherche.trim()} ».
            </p>
            <button
              className="bouton bouton-discret"
              type="button"
              onClick={() => quandRecherche('')}
            >
              Effacer la recherche
            </button>
          </div>
        )}

        {visibles.length > 0 && (
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  {parc.colonnes.map((colonne) => (
                    <th scope="col" className={colonne.classe} key={colonne.cle}>
                      {colonne.entete}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibles.map((ligne) => {
                  const classes = ['rangee'];
                  if (!ligne.actif) classes.push('eteinte');
                  if (fiche.nom === 'prete' && fiche.id === ligne.id)
                    classes.push('choisie');

                  return (
                    <tr
                      className={classes.join(' ')}
                      key={ligne.id}
                      tabIndex={0}
                      onClick={() => quandOuvrir(ligne.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          quandOuvrir(ligne.id);
                        }
                      }}
                    >
                      {parc.colonnes.map((colonne, rang) => {
                        const brut = lisible(ligne, colonne.cle);
                        const lu =
                          brut === null
                            ? brut
                            : colonne.gabarit === true
                              ? (gabarits.find((g) => g.code === brut)?.libelle ?? brut)
                              : colonne.liste === 'types_vehicule'
                                ? (types.find((t) => t.valeur === brut)?.texte ?? brut)
                                : brut;

                        const classes = [
                          colonne.classe,
                          colonne.chiffre === true ? 'code' : undefined,
                        ]
                          .filter((c): c is string => c !== undefined)
                          .join(' ');

                        if (lu === null) {
                          return (
                            <td
                              className={['sourdine', colonne.classe]
                                .filter((c): c is string => c !== undefined)
                                .join(' ')}
                              key={colonne.cle}
                            >
                              non renseigné
                            </td>
                          );
                        }

                        return (
                          <td
                            className={classes === '' ? undefined : classes}
                            key={colonne.cle}
                          >
                            {lu}
                            {colonne.unite !== undefined && (
                              <span className="sourdine"> {colonne.unite}</span>
                            )}
                            {/* La pastille est posée sur la PREMIÈRE colonne,
                                celle qui nomme : c'est là que l'œil va. */}
                            {rang === 0 && !ligne.actif && (
                              <span className="pastille">{inactif(parc)}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {edition.nom === 'aucune' ? (
        <Panneau
          parc={parc}
          etat={fiche}
          gabarits={gabarits}
          types={types}
          quandFermer={quandFermer}
          quandModifier={quandModifier}
          actions={actions}
        />
      ) : (
        <FormulaireFiche
          champs={parc.champs}
          gabarits={choixGabarits}
          types={types}
          titre={
            edition.nom === 'creation'
              ? `${nouveau(parc)} ${parc.singulier}`
              : (edition.initiale[parc.colonneNom] ?? '')
          }
          saisie={edition.saisie}
          fautes={edition.fautes}
          refus={edition.refus}
          envoi={edition.envoi}
          quandChamp={quandChamp}
          quandNomQuitte={() => {
            /* Aucun code n'est proposé dans le parc : une immatriculation ne
               s'invente pas. */
          }}
          quandEnregistrer={quandEnregistrer}
          quandAnnuler={quandAnnuler}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesParc {
  readonly parc: Parc;
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

export function EcranParc({ parc, jeton, quandAccesPerdu }: ProprietesParc) {
  const [liste, setListe] = useState<EtatListe>({ nom: 'chargement' });
  const [fiche, setFiche] = useState<EtatFiche>({ nom: 'aucune' });
  const [edition, setEdition] = useState<Edition>({ nom: 'aucune' });
  const [recherche, setRecherche] = useState('');
  const [gabarits, setGabarits] = useState<readonly Vehicule[]>([]);
  /**
   * Les types de véhicule de la société. Chargés comme les gabarits — une fois,
   * et pour le seul parc qui en porte : celui des véhicules moteurs.
   */
  const [types, setTypes] = useState<readonly Choix[]>([]);
  const [action, setAction] = useState<{
    readonly confirme: boolean;
    readonly envoi: boolean;
    readonly refus: string | null;
  }>({ confirme: false, envoi: false, refus: null });

  /** Un jeton frais, ou rien — auquel cas la séance est finie. */
  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  // La liste, à l'ouverture et à chaque changement de parc.
  useEffect(() => {
    let abandonne = false;
    setListe({ nom: 'chargement' });
    setFiche({ nom: 'aucune' });
    setEdition({ nom: 'aucune' });
    setRecherche('');
    setAction({ confirme: false, envoi: false, refus: null });

    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lu = await lireParc(j, parc);
      if (abandonne) return;
      if (lu.etat === 'ok') {
        setListe({ nom: 'prete', lignes: lu.valeur });
      } else {
        if (lu.cause === 'acces') quandAccesPerdu();
        setListe({ nom: 'panne' });
      }
    })();

    return () => {
      abandonne = true;
    };
  }, [parc, avecJeton, quandAccesPerdu]);

  /**
   * Le référentiel des gabarits, chargé une seule fois et pour les remorques
   * seules : c'est le seul parc qui en porte un. Le charger pour les trois
   * ferait deux appels qui ne serviraient à rien.
   */
  useEffect(() => {
    if (!parc.champs.some((c) => c.liste === 'gabarits')) return;
    let abandonne = false;

    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lu = await lireVehicules(j);
      if (!abandonne && lu.etat === 'ok') setGabarits(lu.valeur);
    })();

    return () => {
      abandonne = true;
    };
  }, [parc, avecJeton]);

  /**
   * Les types de véhicule, même règle : seul le parc des véhicules moteurs en
   * porte. Les DÉSACTIVÉS ne sont pas proposés, mais la valeur enregistrée d'une
   * fiche reste lisible — `choix` la remet en tête dans le formulaire.
   */
  useEffect(() => {
    if (!parc.champs.some((c) => c.liste === 'types_vehicule')) return;
    let abandonne = false;

    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lu = await lireListeReglee(j, 'types');
      if (!abandonne && lu.etat === 'ok') {
        setTypes(
          lu.valeur.filter((t) => t.actif).map((t) => ({ valeur: t.id, texte: t.nom })),
        );
      }
    })();

    return () => {
      abandonne = true;
    };
  }, [parc, avecJeton]);

  /** Range la ligne rendue par l'API dans la liste — jamais la saisie. */
  const poser = useCallback((ligne: LigneParc) => {
    setListe((actuelle) => {
      if (actuelle.nom !== 'prete') return actuelle;
      const connue = actuelle.lignes.some((l) => l.id === ligne.id);
      return {
        nom: 'prete',
        lignes: connue
          ? actuelle.lignes.map((l) => (l.id === ligne.id ? ligne : l))
          : [...actuelle.lignes, ligne],
      };
    });
    setFiche({ nom: 'prete', id: ligne.id, ligne });
  }, []);

  const ouvrir = (id: string) => {
    setEdition({ nom: 'aucune' });
    setAction({ confirme: false, envoi: false, refus: null });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const lu = await lireLigneParc(j, parc, id);
      if (lu.etat === 'ok') {
        setFiche({ nom: 'prete', id, ligne: lu.valeur });
      } else {
        if (lu.cause === 'acces') quandAccesPerdu();
        setFiche({ nom: 'panne', id, cause: lu.cause });
      }
    })();
  };

  const enregistrer = () => {
    if (edition.nom === 'aucune') return;
    const fautes = validerSaisie(edition.saisie, parc);
    if (Object.keys(fautes).length > 0) {
      setEdition({ ...edition, fautes, refus: null });
      return;
    }

    const encours = edition;
    setEdition({ ...encours, fautes: {}, refus: null, envoi: true });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const resultat =
        encours.nom === 'creation'
          ? await creerLigneParc(j, parc, pourCreation(encours.saisie, parc))
          : await modifierLigneParc(
              j,
              parc,
              encours.id as string,
              difference(encours.initiale, encours.saisie, parc),
            );

      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setEdition({ nom: 'aucune' });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setEdition({ ...encours, envoi: false, refus: REFUS[resultat.cause] });
    })();
  };

  const basculer = () => {
    if (fiche.nom !== 'prete') return;
    const { ligne } = fiche;
    setAction({ confirme: false, envoi: true, refus: null });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await changerActiviteParc(j, parc, ligne.id, !ligne.actif);
      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setAction({ confirme: false, envoi: false, refus: null });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setAction({ confirme: false, envoi: false, refus: REFUS[resultat.cause] });
    })();
  };

  const supprimer = () => {
    if (fiche.nom !== 'prete') return;
    const id = fiche.id;
    setAction({ confirme: true, envoi: true, refus: null });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await supprimerLigneParc(j, parc, id);
      if (resultat.etat === 'ok') {
        setListe((actuelle) =>
          actuelle.nom === 'prete'
            ? { nom: 'prete', lignes: actuelle.lignes.filter((l) => l.id !== id) }
            : actuelle,
        );
        setFiche({ nom: 'aucune' });
        setAction({ confirme: false, envoi: false, refus: null });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setAction({ confirme: false, envoi: false, refus: REFUS[resultat.cause] });
    })();
  };

  return (
    <VueParc
      parc={parc}
      types={types}
      liste={liste}
      fiche={fiche}
      edition={edition}
      gabarits={gabarits}
      recherche={recherche}
      quandRecherche={setRecherche}
      quandOuvrir={ouvrir}
      quandFermer={() => {
        setFiche({ nom: 'aucune' });
        setAction({ confirme: false, envoi: false, refus: null });
      }}
      quandCreer={() => {
        const vide = saisieVide(parc);
        setFiche({ nom: 'aucune' });
        setEdition({
          nom: 'creation',
          id: null,
          initiale: vide,
          saisie: vide,
          fautes: {},
          refus: null,
          envoi: false,
        });
      }}
      quandModifier={() => {
        if (fiche.nom !== 'prete') return;
        const depart = saisieDepuis(fiche.ligne, parc);
        setEdition({
          nom: 'modification',
          id: fiche.id,
          initiale: depart,
          saisie: depart,
          fautes: {},
          refus: null,
          envoi: false,
        });
      }}
      quandChamp={(cle, valeur) => {
        setEdition((actuelle) =>
          actuelle.nom === 'aucune'
            ? actuelle
            : { ...actuelle, saisie: { ...actuelle.saisie, [cle]: valeur } },
        );
      }}
      quandEnregistrer={enregistrer}
      quandAnnuler={() => setEdition({ nom: 'aucune' })}
      actions={{
        confirme: action.confirme,
        envoi: action.envoi,
        refus: action.refus,
        quandBasculerActivite: basculer,
        quandDemanderSuppression: () =>
          setAction({ confirme: true, envoi: false, refus: null }),
        quandSupprimer: supprimer,
        quandAnnuler: () => setAction({ confirme: false, envoi: false, refus: null }),
      }}
    />
  );
}
